import React from 'react';
import { motion } from 'framer-motion';
import { useLanguage } from '@/context/LanguageContext';
import SEO from '@/components/SEO';

const CookiePolicy = () => {
  const { language } = useLanguage();

  const content = {
    fr: {
      title: 'Politique des cookies',
      lastUpdated: 'Dernière mise à jour : Juillet 2025',
      intro: `Cette politique des cookies explique comment Byrōn Properties SA utilise les cookies et technologies similaires lorsque vous visitez notre site web.`,
      sections: [
        {
          title: '1. Qu\'est-ce qu\'un cookie ?',
          content: `Un cookie est un petit fichier texte stocké sur votre appareil (ordinateur, tablette, smartphone) lorsque vous visitez un site web. Les cookies permettent au site de se souvenir de vos actions et préférences sur une période donnée, vous évitant ainsi de les saisir à chaque visite.`
        },
        {
          title: '2. Types de cookies utilisés',
          content: `Nous utilisons les catégories de cookies suivantes :

**Cookies essentiels (obligatoires)**
Ces cookies sont indispensables au fonctionnement du site. Ils permettent la navigation et l'utilisation des fonctionnalités de base.
• Authentification et session
• Sécurité
• Préférences de langue

**Cookies analytiques (optionnels)**
Ces cookies nous aident à comprendre comment les visiteurs utilisent notre site.
• Pages visitées
• Durée de visite
• Origine du trafic

**Cookies fonctionnels (optionnels)**
Ces cookies permettent d'améliorer votre expérience.
• Mémorisation de vos préférences
• Personnalisation du contenu`
        },
        {
          title: '3. Durée de conservation',
          content: `• Cookies de session : supprimés à la fermeture du navigateur
• Cookies persistants : jusqu'à 13 mois maximum
• Cookies analytiques : 13 mois`
        },
        {
          title: '4. Cookies tiers',
          content: `Certains cookies peuvent être déposés par des services tiers :

• Google Analytics (analyse d'audience) - optionnel
• Google Maps (cartes interactives) - si vous consultez une propriété avec carte

Ces services ont leurs propres politiques de confidentialité que nous vous invitons à consulter.`
        },
        {
          title: '5. Gestion de vos préférences',
          content: `Lors de votre première visite, une bannière vous permet de :
• Accepter tous les cookies
• Refuser les cookies non essentiels
• Personnaliser vos choix

Vous pouvez modifier vos préférences à tout moment via le lien "Paramètres des cookies" en bas de page.

Vous pouvez également configurer votre navigateur pour :
• Bloquer tous les cookies
• Accepter uniquement certains cookies
• Être averti avant l'installation d'un cookie
• Supprimer les cookies existants`
        },
        {
          title: '6. Impact du refus des cookies',
          content: `Si vous refusez certains cookies :
• Les cookies essentiels resteront actifs (nécessaires au fonctionnement)
• Certaines fonctionnalités pourraient être limitées
• Nous ne pourrons pas personnaliser votre expérience
• Les statistiques d'utilisation seront moins précises`
        },
        {
          title: '7. Base juridique',
          content: `Conformément à la Loi fédérale suisse sur la protection des données (LPD) et à la directive ePrivacy :
• Les cookies essentiels sont déposés sur la base de notre intérêt légitime
• Les cookies analytiques et fonctionnels nécessitent votre consentement préalable`
        },
        {
          title: '8. Contact',
          content: `Pour toute question concernant notre utilisation des cookies :

Byrōn Properties SA
Email : privacy@byron-properties.ch
Téléphone : +41 22 700 12 34`
        }
      ]
    },
    en: {
      title: 'Cookie Policy',
      lastUpdated: 'Last updated: July 2025',
      intro: `This cookie policy explains how Byrōn Properties SA uses cookies and similar technologies when you visit our website.`,
      sections: [
        {
          title: '1. What is a cookie?',
          content: `A cookie is a small text file stored on your device (computer, tablet, smartphone) when you visit a website. Cookies allow the site to remember your actions and preferences over a period of time, saving you from having to re-enter them each time you visit.`
        },
        {
          title: '2. Types of cookies used',
          content: `We use the following categories of cookies:

**Essential cookies (required)**
These cookies are essential for the website to function. They enable navigation and use of basic features.
• Authentication and session
• Security
• Language preferences

**Analytics cookies (optional)**
These cookies help us understand how visitors use our site.
• Pages visited
• Visit duration
• Traffic source

**Functional cookies (optional)**
These cookies help improve your experience.
• Remembering your preferences
• Content personalization`
        },
        {
          title: '3. Retention period',
          content: `• Session cookies: deleted when you close your browser
• Persistent cookies: up to 13 months maximum
• Analytics cookies: 13 months`
        },
        {
          title: '4. Third-party cookies',
          content: `Some cookies may be set by third-party services:

• Google Analytics (audience analysis) - optional
• Google Maps (interactive maps) - if you view a property with a map

These services have their own privacy policies that we encourage you to review.`
        },
        {
          title: '5. Managing your preferences',
          content: `On your first visit, a banner allows you to:
• Accept all cookies
• Refuse non-essential cookies
• Customize your choices

You can change your preferences at any time via the "Cookie settings" link at the bottom of the page.

You can also configure your browser to:
• Block all cookies
• Accept only certain cookies
• Be notified before a cookie is installed
• Delete existing cookies`
        },
        {
          title: '6. Impact of refusing cookies',
          content: `If you refuse certain cookies:
• Essential cookies will remain active (necessary for operation)
• Some features may be limited
• We will not be able to personalize your experience
• Usage statistics will be less accurate`
        },
        {
          title: '7. Legal basis',
          content: `In accordance with the Swiss Federal Act on Data Protection (FADP) and the ePrivacy Directive:
• Essential cookies are set based on our legitimate interest
• Analytics and functional cookies require your prior consent`
        },
        {
          title: '8. Contact',
          content: `For any questions about our use of cookies:

Byrōn Properties SA
Email: privacy@byron-properties.ch
Phone: +41 22 700 12 34`
        }
      ]
    }
  };

  const t = content[language] || content.fr;

  return (
    <main className="min-h-screen bg-byron-bg" data-testid="cookie-policy-page">
      <SEO 
        title={language === 'fr' ? 'Politique des cookies' : 'Cookie Policy'}
        description={language === 'fr' 
          ? 'Politique des cookies de Byrōn Properties. Comment nous utilisons les cookies sur notre site.'
          : 'Byrōn Properties cookie policy. How we use cookies on our website.'}
        canonical="/cookies"
      />

      {/* Header spacer */}
      <div className="header-spacer" />

      {/* Content */}
      <section className="py-10 md:py-14">
        <div className="container-byron">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6 }}
            className="max-w-3xl mx-auto"
          >
            <h1 className="heading-primary text-center mb-4">{t.title}</h1>
            <p className="text-sm text-[#9B9B9B] text-center mb-8">{t.lastUpdated}</p>
            <p className="text-[#6B6B6B] text-center mb-12">{t.intro}</p>

            <div className="space-y-10">
              {t.sections.map((section, index) => (
                <div key={index}>
                  <h2 className="text-lg font-medium text-[#1A1A1A] mb-4">{section.title}</h2>
                  <div className="text-[#6B6B6B] text-sm leading-relaxed whitespace-pre-line">
                    {section.content}
                  </div>
                </div>
              ))}
            </div>
          </motion.div>
        </div>
      </section>
    </main>
  );
};

export default CookiePolicy;
